import { CalendarOutlined, WarningFilled } from "@ant-design/icons"
import { App, Col, DatePicker, Form, Input, Modal, Row, Tooltip } from "antd"
import dayjs from "dayjs"
import React, { useState } from "react"

import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import type { ProjectMilestoneFilterOptions } from "../../../../../redux/project.types.ts"
import type { ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { MILESTONE_STATUSES } from "../../../../constants/milestone-statuses.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../../../../constants/project-entity-category.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { PlannedMoneyInput } from "../../../../../components/PlannedMoneyInput.tsx"

interface ProjectMilestoneModalProps {
  open: boolean
  options: ProjectMilestoneFilterOptions
  onClose: () => void
  onSubmit: (data: ProjectMilestone) => Promise<{ ok: boolean }>
}

const DEFAULT_VALUES: Partial<ProjectMilestone> = {
  name: "",
  status: MILESTONE_STATUSES.PENDING,
  description: "",
  planned_receivable: 0,
  start_date: null,
  end_date: null,
  actual_end_date: null,
  milestone_category: PROJECT_ENTITY_CATEGORIES.STANDARD
}

export const ProjectMilestoneModal = ({
  open,
  options,
  onClose,
  onSubmit
}: ProjectMilestoneModalProps) => {
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectMilestone>()
  const { modal } = App.useApp()

  const watch = Form.useWatch(
    (s) => ({
      status: s?.status,
      actualEnd: s?.actual_end_date,
      plannedEnd: s?.end_date
    }),
    form
  )

  const isCompleted = watch?.status === MILESTONE_STATUSES.COMPLETED
  const completedWithoutActualEnd = isCompleted && !watch?.actualEnd
  const isLate =
    watch?.actualEnd &&
    watch?.plannedEnd &&
    dayjs(watch.actualEnd).isAfter(dayjs(watch.plannedEnd))

  const handleSubmit = async () => {
    try {
      const values = await validateFormWithScroll(form)

      setSaving(true)

      const res = await onSubmit({
        ...values,
        end_date: normalizeDate(values?.end_date),
        actual_end_date: normalizeDate(values?.actual_end_date),
        start_date: normalizeDate(values?.start_date)
      })
      if (!res?.ok) return

      form.resetFields()
      onClose()
    } catch (error: any) {
      if (!error?.errorFields) throw error
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const isChanged = isFormChanged<ProjectMilestone>(
      form.getFieldsValue(),
      DEFAULT_VALUES as ProjectMilestone
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          form.resetFields()
          onClose()
        }
      })
    } else {
      form.resetFields()
      onClose()
    }
  }

  const onStatusChangeHandler = (value: number) => {
    if (value === MILESTONE_STATUSES.COMPLETED) {
      if (!form.getFieldValue("actual_end_date")) {
        form.setFieldValue("actual_end_date", dayjs().format(DATE_FORMAT))
      }
    }
  }

  return (
    <Modal
      open={open}
      title={"Add Milestone"}
      okText={"Create"}
      cancelText="Cancel"
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={saving}
      destroyOnHidden
      width={600}
      maskClosable={false}
      keyboard={false}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={saving}
        className="mt-4"
        initialValues={DEFAULT_VALUES}
        validateTrigger={["onSubmit", "onChange"]}
      >
        {/* Milestone Name */}
        <Form.Item<ProjectMilestone>
          name="name"
          label="Milestone Name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Milestone name is required"
            },
            { max: 255, message: "Must be at most 255 characters" }
          ]}
        >
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 3 }}
            placeholder="Enter milestone name"
            autoFocus
          />
        </Form.Item>

        <Row>
          <Col sm={24} md={16}>
            <Tooltip
              title={
                completedWithoutActualEnd
                  ? "Milestone should be completed with actual end date"
                  : ""
              }
              color="orange"
            >
              <Form.Item<ProjectMilestone>
                name="status"
                label="Status"
                rules={[{ required: true, message: "Required" }]}
              >
                <ResolvedSelect
                  status={completedWithoutActualEnd ? "warning" : ""}
                  placeholder="Select status"
                  options={options?.["status"]}
                  onChange={onStatusChangeHandler}
                />
              </Form.Item>
            </Tooltip>
          </Col>
          <Col sm={24} md={8}>
            <Form.Item<ProjectMilestone> name="milestone_category" label="Type">
              <ResolvedSelect
                placeholder="Select type"
                options={options?.["category"]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Dates */}
        <div className="grid grid-cols-3 gap-4">
          {/* Planned Start */}
          <Form.Item<ProjectMilestone>
            name="start_date"
            label="Planned Start"
            getValueProps={getDateValueProps}
            dependencies={["end_date"]}
            rules={createDateRangeRules<ProjectMilestone>("end_date", "start")}
          >
            <DatePicker
              placeholder="Select date"
              className="w-full"
              onChange={dateAutoFill<ProjectMilestone>({
                form,
                endDatePath: ["end_date"]
                // additionalEndDatePaths: ["actual_end_date"]
              })}
            />
          </Form.Item>

          {/* Planned End */}
          <Form.Item<ProjectMilestone>
            name="end_date"
            label="Planned End"
            getValueProps={getDateValueProps}
            dependencies={["start_date"]}
            rules={createDateRangeRules<ProjectMilestone>("start_date", "end")}
          >
            <DatePicker placeholder="Select date" className="w-full" />
          </Form.Item>

          {/* Actual End */}
          <Tooltip
            title={
              completedWithoutActualEnd
                ? "Milestone should be completed with actual end date"
                : ""
            }
            color="orange"
          >
            <Form.Item<ProjectMilestone>
              name="actual_end_date"
              label="Actual End"
              getValueProps={getDateValueProps}
            >
              <DatePicker
                status={completedWithoutActualEnd ? "warning" : ""}
                placeholder="Select date"
                className="w-full"
                suffixIcon={
                  isLate ? (
                    <WarningFilled className="text-warning" />
                  ) : (
                    <CalendarOutlined />
                  )
                }
              />
            </Form.Item>
          </Tooltip>
        </div>

        {/* Planned Receivable */}
        <Tooltip title="Planned receivable">
          <PlannedMoneyInput
            name="planned_receivable"
            label="Planned Receivable"
          />
        </Tooltip>

        {/* Planned Payable */}
        <Tooltip title="Planned payable. Enter the payable lines by hand; the row flags Committed when it differs from this figure.">
          <PlannedMoneyInput name="planned_payable" label="Planned Payable" />
        </Tooltip>

        {/* Description */}
        <Form.Item<ProjectMilestone>
          name="description"
          label="Description"
          rules={[
            {
              whitespace: true,
              max: 250,
              message: "Must be at most 250 characters"
            }
          ]}
        >
          <Input.TextArea
            rows={3}
            autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder="Enter description"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
